import { searchOrganisations } from '@infuse/api/iam/organisation';
import { searchTenants } from '@infuse/api/iam/tenant';

export interface MoveDestination {
  id: string;
  name: string;
  tenantId?: string;
}

export interface MoveDestinations {
  organisations: MoveDestination[];
  tenants: MoveDestination[];
}

/**
 * Fetches all available destinations (tenants and organisations) for moving users.
 * Returns both domain-level and tenant-level organisations.
 * Automatically handles pagination to fetch all items.
 */
export async function fetchMoveDestinations(): Promise<MoveDestinations> {
  const allTenants: MoveDestination[] = [];
  const allOrganisations: MoveDestination[] = [];

  // Fetch all tenants with pagination
  try {
    let tenantPage = 0;
    let hasMoreTenants = true;
    while (hasMoreTenants) {
      const tenantsResponse = await searchTenants({ size: 100, page: tenantPage });
      allTenants.push(
        ...tenantsResponse.content.map((tenant) => ({
          id: tenant.id,
          name: tenant.name
        }))
      );
      hasMoreTenants = !tenantsResponse.last;
      tenantPage++;
    }
  } catch (error) {
    console.error('Failed to fetch tenants for move destinations:', error);
    // Continue with empty tenants list
  }

  // Fetch all organisations with pagination
  try {
    let orgPage = 0;
    let hasMoreOrgs = true;
    while (hasMoreOrgs) {
      const orgsResponse = await searchOrganisations({ size: 100, page: orgPage });
      allOrganisations.push(
        ...orgsResponse.content.map((org) => ({
          id: org.id,
          name: org.name,
          tenantId: org.tenantId
        }))
      );
      hasMoreOrgs = !orgsResponse.last;
      orgPage++;
    }
  } catch (error) {
    console.error('Failed to fetch organisations for move destinations:', error);
    // Continue with empty organisations list
  }

  return { organisations: allOrganisations, tenants: allTenants };
}
